import React, { useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ChevronLeftIcon, RocketLaunchIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { toast } from 'react-hot-toast'
import ClubOnboardingWizard from './ClubOnboardingWizard'
import { createPlatformAdminService } from './onboardingWizardServices'

/**
 * Platform-admin variant of the onboarding wizard. Same UI; data persists to
 * pending_tenant_setups on the control-plane DB. A "Provision tenant" panel
 * sits above the wizard for setups in submitted/failed status — it triggers
 * /api/admin/pending-tenant-setups/:id/provision which calls tenant-provisioning
 * internally and hydrates operational_config into the new tenant DB.
 */
export default function NewTenantSetupWizard() {
  const { setupId } = useParams()
  const navigate = useNavigate()
  const service = useMemo(() => createPlatformAdminService(setupId), [setupId])
  const [provisioning, setProvisioning] = useState(false)
  const [adminForm, setAdminForm] = useState({ username: '', email: '', password: '', sendWelcomeEmail: false })
  const [showProvision, setShowProvision] = useState(false)

  const provision = async () => {
    if (!adminForm.username || !adminForm.email) {
      toast.error('Admin username + email required')
      return
    }
    if (adminForm.sendWelcomeEmail && !adminForm.email.trim()) {
      toast.error('Admin email is required to send a welcome email')
      return
    }
    setProvisioning(true)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/admin/pending-tenant-setups/${setupId}/provision`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin: {
            ...adminForm,
            send_welcome_email: adminForm.sendWelcomeEmail,
          },
          createDatabase: true,
        }),
      })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'provision failed')
      toast.success(`Tenant provisioned: ${j.provisionedGroupId}`)
      navigate('/admin/pending-tenant-setups')
    } catch (e) {
      toast.error(e.message)
    } finally {
      setProvisioning(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-4 pb-16">
      <div className="mb-4 flex items-center gap-2">
        <Link to="/admin/pending-tenant-setups" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
          <ChevronLeftIcon className="h-4 w-4" />
          Back to pending setups
        </Link>
        <span className="text-gray-400">·</span>
        <span className="text-xs font-mono text-gray-500">setup #{setupId}</span>
      </div>

      <div className="mb-6 p-4 rounded-md border border-amber-300 bg-amber-50 text-sm">
        <div className="flex items-start gap-3">
          <RocketLaunchIcon className="h-5 w-5 text-amber-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">Platform-admin onboarding (pre-tenant)</div>
            <p className="text-amber-900 mt-1">
              You're filling in a tenant's data <strong>before</strong> their tenant DB exists.
              Saves go to the control-plane staging table. When the data looks right, expand{' '}
              <button type="button" onClick={() => setShowProvision((v) => !v)} className="underline">
                Provision tenant
              </button>{' '}
              below, supply an admin login, and the tenant + venue_configuration will be created in one step.
            </p>
            {showProvision && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl">
                <input
                  type="text"
                  placeholder="Admin username"
                  className="input-field w-full"
                  value={adminForm.username}
                  onChange={(e) => setAdminForm((f) => ({ ...f, username: e.target.value }))}
                />
                <input
                  type="email"
                  placeholder="Admin email"
                  className="input-field w-full"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))}
                />
                <input
                  type="password"
                  placeholder="Initial password"
                  className="input-field w-full"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
                />
                <label className="flex items-start gap-2 md:col-span-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={adminForm.sendWelcomeEmail}
                    disabled={!adminForm.email.trim()}
                    onChange={(e) => setAdminForm((f) => ({ ...f, sendWelcomeEmail: e.target.checked }))}
                  />
                  <span>
                    Send welcome email with login details
                    {!adminForm.email.trim() && (
                      <span className="block text-xs text-amber-800/80">Enter admin email to enable.</span>
                    )}
                  </span>
                </label>
                <button
                  type="button"
                  onClick={provision}
                  disabled={provisioning}
                  className="md:col-span-3 inline-flex items-center justify-center gap-1 px-4 py-2 text-sm bg-amber-700 text-white rounded-md disabled:opacity-50 max-w-sm"
                >
                  {provisioning ? 'Provisioning…' : 'Provision tenant + hydrate'}
                  <RocketLaunchIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ClubOnboardingWizard service={service} />
    </div>
  )
}
